import { supabase } from './supabase';
import type { RegistryItem } from '../types/registry';

export interface PayPalOrderRequest {
  itemId: string;
  itemName: string;
  originalAmount: number;
  currency: string;
  feeResponsibility: 'COUPLE' | 'GUEST' | 'SPLIT';
  platformFeePercentage: number;
  gatewayFeePercentage: number;
  registryId: string;
  registryOwnerId: string;
  payerFirstName: string;
  payerLastName: string;
  payerEmail: string;
  guestId?: string;
  contributionMessage?: string;
  isGroupGift?: boolean;
  payeeEmail?: string;
}

export interface PayPalOrderResponse {
  orderId: string;
  finalAmount: number;
}

export function calculateFinalAmount(
  originalAmount: number,
  feeResponsibility: 'COUPLE' | 'GUEST' | 'SPLIT',
  platformFeePercentage: number,
  gatewayFeePercentage: number
): { finalAmount: number; platformFee: number; gatewayFee: number; totalFees: number } {
  const platformFeeRate = platformFeePercentage / 100;
  const gatewayFeeRate = gatewayFeePercentage / 100;
  const platformFee = originalAmount * platformFeeRate;
  const gatewayFee = originalAmount * gatewayFeeRate;
  const totalFees = platformFee + gatewayFee;

  let finalAmount = originalAmount;
  switch (feeResponsibility) {
    case 'GUEST':
      finalAmount = originalAmount + totalFees;
      break;
    case 'COUPLE':
      finalAmount = originalAmount;
      break;
    case 'SPLIT':
      finalAmount = originalAmount + (totalFees / 2);
      break;
  }

  return {
    finalAmount: Number(finalAmount.toFixed(2)),
    platformFee: Number(platformFee.toFixed(2)),
    gatewayFee: Number(gatewayFee.toFixed(2)),
    totalFees: Number(totalFees.toFixed(2))
  };
}

export async function createPayPalOrder(request: PayPalOrderRequest): Promise<PayPalOrderResponse> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const response = await fetch(`${supabaseUrl}/functions/v1/paypal-create-order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create PayPal order');
  }

  return response.json();
}

export async function capturePayPalOrder(orderId: string): Promise<{ success: boolean }> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const response = await fetch(`${supabaseUrl}/functions/v1/paypal-capture-order`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({ orderId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to capture PayPal payment');
  }

  return response.json();
}

export async function loadFeeSettings(): Promise<{ platformFee: number; gatewayFee: number }> {
  const { data: platformFeeData } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', 'platform_fee_rate')
    .maybeSingle();

  const { data: gatewayFeeData } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', 'gateway_fee_rate')
    .maybeSingle();

  return {
    platformFee: parseFloat(platformFeeData?.value || '0'),
    gatewayFee: parseFloat(gatewayFeeData?.value || '0')
  };
}
